import {
  Avatar,
  Card,
  Col,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Empty,
  Typography,
} from 'antd';
import {
  BarChartOutlined,
  ExperimentOutlined,
  LineChartOutlined,
  RiseOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import { api } from '../api/client';
import { useBatches } from '../hooks/useBatches';
import { ALL_CELLS, BOXES } from '../lib/ninebox-config';

const STORE_FILTER_ALL = 'all';

/** 与 API storeKeyOf 同源：优先 Employee.store，否则 deptPath 第 2 段 */
function storeKeyOf(emp: { store?: string | null; deptPath?: string | null }): string | null {
  const store = (emp.store ?? '').trim();
  if (store) return store;
  const segments = (emp.deptPath ?? '')
    .split('/')
    .map((s) => s.trim())
    .filter(Boolean);
  return segments.length >= 2 ? segments[1]! : null;
}

function filterNineboxGridByStore(
  grid: Record<string, any[]>,
  storeFilter: string,
): Record<string, any[]> {
  if (storeFilter === STORE_FILTER_ALL) return grid;
  const out: Record<string, any[]> = {};
  for (const [cell, rows] of Object.entries(grid)) {
    out[cell] = (rows ?? []).filter((r) => {
      const emp = r?.employee ?? {};
      return storeKeyOf({ store: emp.store, deptPath: emp.deptPath }) === storeFilter;
    });
  }
  return out;
}

/**
 * 总览看板（Phase 2 入口）。
 *
 * 一屏呈现：
 *  - 素质项完成 + 可疑质检；胜任力与高潜合并完成（同屏作答）+ 晋升提名
 *  - 九宫格分布（与 NineBoxPage 完全同源：rank / 名称 / 参考占比 / 实际占比 + 头像）
 *  - 晋升提名建议分布饼图
 *  - 各部门/门店素质项任务完成率柱状图
 *
 * 数据源：
 *  - GET /admin/overview?batchId=&storeKey=  （KPI / 完成率柱图 / 提名分布）
 *  - GET /admin/ninebox?batchId=   （九宫格员工明细；单店时前端按 store 过滤）
 *  - GET /admin/store-progress?batchId= （门店下拉选项）
 */
export function OverviewPage() {
  const { batches, batchId, setBatchId } = useBatches();
  const [storeFilter, setStoreFilter] = useState<string>(STORE_FILTER_ALL);

  useEffect(() => {
    setStoreFilter(STORE_FILTER_ALL);
  }, [batchId]);

  const { data: storeProgressData } = useQuery({
    queryKey: ['store-progress', batchId],
    queryFn: () => api.get('/admin/store-progress', { params: { batchId } }),
    enabled: !!batchId,
  });

  const storeOptions = useMemo(() => {
    const stores = ((storeProgressData as any)?.stores ?? []) as Array<{
      storeKey: string;
      storeLabel: string;
      unresolved?: boolean;
    }>;
    return [
      { label: '全部门店', value: STORE_FILTER_ALL },
      ...stores
        .filter((s) => !s.unresolved)
        .map((s) => ({ label: s.storeLabel || s.storeKey, value: s.storeKey })),
    ];
  }, [storeProgressData]);

  const { data, isLoading } = useQuery({
    queryKey: ['overview', batchId, storeFilter],
    queryFn: () =>
      api.get('/admin/overview', { params: { batchId, storeKey: storeFilter } }),
    enabled: !!batchId,
  });

  /**
   * 与「九宫格」页同源拉取 /admin/ninebox，拿到每格完整员工列表，
   * 让总览九宫格也展示头像与参考占比，避免与详情页两套口径。
   * 全部门店：不过滤；单店：按 storeKeyOf 过滤。
   */
  const { data: nineboxData, isLoading: nineboxLoading } = useQuery({
    queryKey: ['ninebox', batchId],
    queryFn: () => api.get('/admin/ninebox', { params: { batchId } }),
    enabled: !!batchId,
  });

  const o = data as any;
  const aggregationAxis: 'store' | 'dept' =
    o?.aggregationAxis === 'dept' ? 'dept' : storeFilter === STORE_FILTER_ALL ? 'store' : 'dept';
  const dimLabel = aggregationAxis === 'store' ? '门店' : '部门';
  const dimTitlePrefix = aggregationAxis === 'store' ? '各门店' : '各部门';
  const axisName = aggregationAxis === 'store' ? '门店' : '部门/姓名';

  const nineboxGrid: Record<string, any[]> = useMemo(
    () => filterNineboxGridByStore((nineboxData as any)?.grid || {}, storeFilter),
    [nineboxData, storeFilter],
  );

  return (
    <div>
      <div className="tg-page-title">总览看板</div>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder="选择批次"
            style={{ width: 200 }}
            value={batchId}
            options={batches.map((b) => ({ label: b.name, value: b.id }))}
            onChange={setBatchId}
          />
          <Select
            placeholder="门店"
            style={{ width: 200 }}
            value={storeFilter}
            options={storeOptions}
            onChange={setStoreFilter}
            disabled={!batchId}
          />
          <Tag color="purple">可视化总览</Tag>
        </Space>

        {!batchId ? (
          <Empty description="请选择批次" />
        ) : (
          <>
            <Card
              title={
                <Space>
                  <LineChartOutlined />
                  <span>各评价维度提交情况</span>
                </Space>
              }
              bordered={false}
              style={{
                marginBottom: 16,
                background: 'linear-gradient(135deg,#f8faff 0%,#ffffff 50%,#faf6ff 100%)',
                border: '1px solid rgba(22,119,255,0.08)',
              }}
              loading={isLoading}
            >
              <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 11, lineHeight: 1.55 }}>
                第一列素质项；第二列胜任力与高潜<strong>合并作答</strong>一条进度；后两列为提名与可疑提交。均与下方图表口径一致
                {storeFilter === STORE_FILTER_ALL
                  ? '（全部门店：仅含有二级部门的门店）'
                  : `（当前门店：${storeOptions.find((s) => s.value === storeFilter)?.label ?? storeFilter}）`}
                。
              </Typography.Paragraph>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: 14,
                  alignItems: 'stretch',
                }}
              >
                <KpiTile
                  icon={<BarChartOutlined />}
                  title="素质项评价完成率"
                  subtitle={
                    typeof o?.evalTasks === 'number'
                      ? `已提交 ${o?.evalDone ?? 0} / ${o.evalTasks} 条`
                      : undefined
                  }
                  loading={isLoading}
                  accent="#1677ff"
                  tint="linear-gradient(145deg, rgba(22,119,255,0.14) 0%, rgba(22,119,255,0.05) 100%)"
                >
                  <Statistic
                    value={o?.completion ?? 0}
                    suffix="%"
                    valueStyle={{ color: '#1677ff', fontSize: 28 }}
                  />
                  <Progress percent={o?.completion ?? 0} showInfo={false} strokeColor="#1677ff" size="small" />
                </KpiTile>
                <KpiTile
                  icon={<ExperimentOutlined />}
                  title="胜任力与高潜（合并作答）"
                  subtitle={`已对 ${o?.bothSubmittedCount ?? 0}/${o?.competencyTasks ?? 0} · 胜任力 ${o?.competencyDone ?? 0}/${o?.competencyTasks ?? 0} · 高潜表 ${o?.hpSubmittedCount ?? 0}/${o?.competencyTasks ?? 0} · 标记高潜 ${o?.hpCount ?? 0}`}
                  loading={isLoading}
                  accent="#722ed1"
                  tint="linear-gradient(145deg, rgba(114,46,209,0.14) 0%, rgba(114,46,209,0.05) 100%)"
                >
                  <Statistic
                    value={o?.competencyHpCombinedPct ?? 0}
                    suffix="%"
                    valueStyle={{ color: '#722ed1', fontSize: 28 }}
                  />
                  <Progress
                    percent={o?.competencyHpCombinedPct ?? 0}
                    showInfo={false}
                    strokeColor="#722ed1"
                    size="small"
                  />
                </KpiTile>
                <KpiTile
                  icon={<RiseOutlined />}
                  title="晋升提名数"
                  subtitle="本批次「已提交」记录条数"
                  loading={isLoading}
                  accent="#52c41a"
                  tint="linear-gradient(145deg, rgba(82,196,26,0.14) 0%, rgba(82,196,26,0.05) 100%)"
                >
                  <Statistic value={o?.nominationCount ?? 0} valueStyle={{ color: '#52c41a', fontSize: 28 }} />
                </KpiTile>
                <KpiTile
                  icon={<WarningOutlined />}
                  title="可疑提交数"
                  subtitle="素质项用时 ≤20 秒（辅助质检）"
                  loading={isLoading}
                  accent="#ff4d4f"
                  tint="linear-gradient(145deg, rgba(255,77,79,0.12) 0%, rgba(255,77,79,0.04) 100%)"
                >
                  <Statistic value={o?.suspiciousCount ?? 0} valueStyle={{ color: '#ff4d4f', fontSize: 28 }} />
                </KpiTile>
              </div>
            </Card>

            <Row gutter={16} style={{ marginTop: 0 }}>
              <Col span={12}>
                <Card
                  title="九宫格"
                  loading={isLoading || nineboxLoading}
                  extra={
                    <Space>
                      <Tag color="blue">
                        总人数{' '}
                        {ALL_CELLS.reduce((s, c) => s + (nineboxGrid[c]?.length || 0), 0)}
                      </Tag>
                      <Link to={`/ninebox?batchId=${batchId ?? ''}`}>查看详情 →</Link>
                    </Space>
                  }
                >
                  <NineBoxMini grid={nineboxGrid} />
                </Card>
              </Col>
              <Col span={12}>
                <Card
                  title={`${dimTitlePrefix}被提名人数占比`}
                  loading={isLoading}
                  extra={<Tag color="green">已提交 {o?.nominationCount ?? 0} 条</Tag>}
                >
                  {(o?.nominationCount ?? 0) === 0 ? (
                    <Empty description="暂无已提交的晋升提名" />
                  ) : (
                    <>
                      <Typography.Paragraph
                        type="secondary"
                        style={{ marginBottom: 10, fontSize: 11, lineHeight: 1.55 }}
                      >
                        环形图按<strong>被提名人</strong>花名册
                        <strong>{aggregationAxis === 'store' ? '门店' : '末级部门'}</strong>
                        聚合：各{dimLabel}人数占本筛选范围内已提交提名总数的比例。
                      </Typography.Paragraph>
                      {Array.isArray(o?.nominationNominees) && o.nominationNominees.length > 0 ? (
                        <div style={{ marginBottom: 10 }}>
                          <Typography.Text type="secondary" style={{ fontSize: 11, marginRight: 8 }}>
                            被提名人员：
                          </Typography.Text>
                          <Space size={[4, 6]} wrap>
                            {(o.nominationNominees as { nomineeName: string }[]).map((x, i) => (
                              <Tag key={`${x.nomineeName}-${i}`} color="processing">
                                {x.nomineeName}
                              </Tag>
                            ))}
                          </Space>
                        </div>
                      ) : null}
                      <ReactECharts option={buildNominationDeptPie(o?.nominationByDept)} style={{ height: 300 }} />
                      <div
                        style={{
                          fontSize: 11,
                          color: '#8c8c8c',
                          textAlign: 'center',
                          marginTop: 4,
                          lineHeight: 1.55,
                        }}
                      >
                        提名建议分项：{formatNominationBreakdown(o?.nominations)}
                      </div>
                    </>
                  )}
                </Card>
              </Col>
            </Row>

            <Card
              title={`${dimTitlePrefix} · 素质项评价任务完成率`}
              style={{ marginTop: 16 }}
              loading={isLoading}
            >
              <Typography.Paragraph type="secondary" style={{ marginBottom: 10, fontSize: 11, lineHeight: 1.55 }}>
                仅素质项任务：已提交条数 ÷ 派发条数；
                {aggregationAxis === 'store'
                  ? '维度＝被评价人所在门店（仅含有二级部门的门店）。'
                  : '有二级部门按末级部门；无二级部门的人按姓名各占一根柱。'}
              </Typography.Paragraph>
              <ReactECharts
                option={buildDeptBar(o?.deptCompletion, { axisName })}
                style={{ height: 360 }}
              />
            </Card>

            <Card
              title={`${dimTitlePrefix} · 胜任力与高潜合并作答完成率${aggregationAxis === 'dept' ? '（末级部门）' : ''}`}
              style={{ marginTop: 16 }}
              loading={isLoading}
            >
              <Typography.Paragraph type="secondary" style={{ marginBottom: 10, fontSize: 11, lineHeight: 1.55 }}>
                各{dimLabel}需评价人数中，胜任力与高潜表单<strong>均已提交</strong>的人数占比（同一作答流程）；
                {aggregationAxis === 'store'
                  ? '按花名册门店聚合。'
                  : '有二级部门按末级部门；无二级部门按姓名。'}
              </Typography.Paragraph>
              {(o?.competencyHpDeptCombined ?? []).length === 0 ? (
                <Empty description={`暂无${dimLabel}数据`} />
              ) : (
                <ReactECharts
                  option={buildDeptBar(o?.competencyHpDeptCombined ?? [], {
                    color: '#722ed1',
                    valueHint: '胜任力+高潜合并完成率',
                    axisName,
                  })}
                  style={{ height: 380 }}
                />
              )}
            </Card>
          </>
        )}
      </Card>
    </div>
  );
}

function KpiTile(props: {
  title: string;
  subtitle?: string;
  accent: string;
  tint: string;
  loading?: boolean;
  icon?: ReactNode;
  children: React.ReactNode;
}) {
  const { title, subtitle, accent, tint, loading, icon, children } = props;
  return (
    <Card
      loading={loading}
      size="small"
      style={{ height: '100%' }}
      styles={{
        body: {
          padding: '14px 14px 12px',
          borderLeft: `5px solid ${accent}`,
          background: tint,
          borderRadius: 10,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
        {icon ? (
          <span style={{ color: accent, fontSize: 18, lineHeight: 1.2 }} aria-hidden>
            {icon}
          </span>
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1f1f1f' }}>{title}</div>
        </div>
      </div>
      {subtitle ? (
        <div style={{ fontSize: 10, color: '#8c8c8c', marginBottom: 8, lineHeight: 1.4 }}>
          {subtitle}
        </div>
      ) : (
        <div style={{ marginBottom: 8 }} />
      )}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
        {children}
      </div>
    </Card>
  );
}

/**
 * 总览九宫格：与 NineBoxPage 同源 /admin/ninebox 数据 → grid: Record<finalCell, 员工列表>。
 * 每格显示：等级编号、名称、参考占比、实际占比+人数、头像（最多 6 个 + 折叠数）。
 *
 * 纵轴：素质项等级（图中自上而下为高→低，对应 H/M/L）；
 * 横轴：业绩等级（自左而右为低→高，对应 L/M/H）。
 */
function NineBoxMini({ grid }: { grid: Record<string, any[]> }) {
  const total = ALL_CELLS.reduce((s, c) => s + (grid[c]?.length || 0), 0);
  const Y_LABELS = ['高', '中', '低'];
  const X_LABELS = ['低', '中', '高'];
  return (
    <div style={{ padding: '4px 4px 0' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '72px 1fr',
          gridTemplateRows: '1fr auto',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            gridColumn: '1 / 2',
            gridRow: '1 / 2',
            display: 'grid',
            gridTemplateRows: 'repeat(3, 1fr)',
            position: 'relative',
            paddingRight: 6,
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              writingMode: 'vertical-rl',
              fontSize: 12,
              color: '#595959',
              letterSpacing: 2,
              fontWeight: 600,
            }}
          >
            素质项等级
          </div>
          {Y_LABELS.map((l) => (
            <div
              key={l}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                fontSize: 11,
                color: '#888',
                paddingRight: 4,
              }}
            >
              {l}
            </div>
          ))}
        </div>
        <div
          style={{
            gridColumn: '2 / 3',
            gridRow: '1 / 2',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: 'repeat(3, 132px)',
            gap: 8,
          }}
        >
          {BOXES.map((box) => {
            const employees = box.cells.flatMap((c) => grid[c] || []);
            const n = employees.length;
            const pct = total > 0 ? (n / total) * 100 : 0;
            const visibleAvatars = employees.slice(0, 6);
            const overflow = Math.max(0, employees.length - visibleAvatars.length);
            return (
              <div
                key={box.id}
                style={{
                  gridArea: box.gridArea,
                  background: box.bg,
                  color: '#fff',
                  borderRadius: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px 8px',
                  textAlign: 'center',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 4,
                    left: 6,
                    fontSize: 11,
                    fontWeight: 700,
                    opacity: 0.85,
                    background: 'rgba(0,0,0,0.18)',
                    padding: '0 6px',
                    borderRadius: 6,
                    lineHeight: '16px',
                  }}
                >
                  {box.rank}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    lineHeight: 1.25,
                    opacity: 0.98,
                    marginTop: 2,
                  }}
                >
                  {box.name}
                </div>
                <div style={{ fontSize: 10, marginTop: 4, opacity: 0.86 }}>
                  参考：{box.ref}
                </div>
                <div style={{ fontSize: 11, marginTop: 2, opacity: 0.92 }}>
                  实际：{pct.toFixed(2)}%（{n} 人）
                </div>
                {employees.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      gap: 3,
                      marginTop: 6,
                    }}
                  >
                    {visibleAvatars.map((item: any) => (
                      <Avatar
                        key={item.id}
                        size={18}
                        src={item.employee?.avatar}
                        style={{
                          background: 'rgba(255,255,255,0.25)',
                          color: '#fff',
                          fontSize: 10,
                        }}
                      >
                        {item.employee?.name?.[0]}
                      </Avatar>
                    ))}
                    {overflow > 0 && (
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.25)',
                          color: '#fff',
                          fontSize: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        +{overflow}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ gridColumn: '1 / 2', gridRow: '2 / 3' }} />
        <div
          style={{
            gridColumn: '2 / 3',
            gridRow: '2 / 3',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            paddingTop: 8,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 8,
            }}
          >
            {X_LABELS.map((l) => (
              <div
                key={l}
                style={{
                  textAlign: 'center',
                  fontSize: 11,
                  color: '#888',
                }}
              >
                {l}
              </div>
            ))}
          </div>
          <div
            style={{
              textAlign: 'center',
              fontSize: 12,
              color: '#595959',
              marginTop: 6,
              letterSpacing: 1,
              fontWeight: 600,
            }}
          >
            业绩等级（低 → 高）
          </div>
        </div>
      </div>
      <div
        style={{
          marginTop: 10,
          textAlign: 'right',
          fontSize: 12,
          color: '#888',
        }}
      >
        覆盖人数 {total}（按素质档次 × 业绩档次落格）
      </div>
    </div>
  );
}

const NOMINATION_SUGGESTION_ORDER = ['UP_1', 'UP_2_BREAKTHROUGH', 'HR_DECIDE'] as const;

function formatNominationBreakdown(arr: Array<{ suggestion: string; count: number }> = []) {
  const LABELS: Record<string, string> = {
    UP_1: '晋升 1 级',
    UP_2_BREAKTHROUGH: '破格晋升',
    HR_DECIDE: 'HR 合议拟定',
  };
  const byKey = Object.fromEntries(arr.map((x) => [x.suggestion, x.count]));
  return NOMINATION_SUGGESTION_ORDER.map((k) => `${LABELS[k]} ${byKey[k] ?? 0} 条`).join(' · ');
}

const NOM_DEPT_PIE_COLORS = [
  '#1677ff',
  '#52c41a',
  '#fa8c16',
  '#722ed1',
  '#eb2f96',
  '#13c2c2',
  '#faad14',
  '#f5222d',
];

function buildNominationDeptPie(arr: Array<{ deptName: string; count: number }> = []) {
  const data = (arr ?? [])
    .filter((x) => x.count > 0)
    .map((x) => ({ name: x.deptName, value: x.count }));
  return {
    tooltip: {
      trigger: 'item',
      formatter: '{b}<br/>被提名：{c} 人（{d}%）',
    },
    legend: { bottom: 0, type: 'scroll' },
    color: NOM_DEPT_PIE_COLORS,
    series: [
      {
        type: 'pie',
        radius: ['40%', '70%'],
        data,
        label: { formatter: '{b}\n{d}%' },
      },
    ],
  };
}

function buildDeptBar(
  arr: Array<{ deptName: string; completion: number }> = [],
  opts?: { color?: string; valueHint?: string; axisName?: string },
) {
  const color = opts?.color ?? '#1677ff';
  const hint = opts?.valueHint ?? '素质项评价任务完成率';
  const axisName = opts?.axisName ?? '末级部门';
  return {
    tooltip: {
      trigger: 'axis',
      valueFormatter: (v: number | string) => `${v}%（${hint}）`,
    },
    grid: { left: 56, right: 20, bottom: 80, top: 28 },
    xAxis: {
      type: 'category',
      name: axisName,
      data: arr.map((x) => x.deptName),
      axisLabel: { rotate: 30 },
    },
    yAxis: {
      type: 'value',
      max: 100,
      name: '完成率',
      axisLabel: { formatter: '{value}%' },
    },
    series: [
      {
        type: 'bar',
        data: arr.map((x) => x.completion),
        itemStyle: { color },
        label: { show: true, position: 'top', formatter: '{c}%' },
      },
    ],
  };
}
